import dayjs from '@/dayjs';
import filters from '@/filters';
import { combineDateTime, formatDate } from '@/helpers/dates';
import { hideTooltip, showTooltip } from '@/helpers/ui';
import i18n from '@/i18n';
import calendarInterface from '@/modules/calendar/calendar-interface';
import { useCalendarFiltersStore } from '@/stores/calendar-filters';
import { useCompanyStore } from '@/stores/company';
import { useResourcesStore } from '@/stores/resources';
import { useThemeStore } from '@/stores/theme';
import createItem from './CreateCalendarItem';
import createDayHeaderContent from './CreateDayHeaderContent';
import createResourceLabel from './CreateResourceLabel';

export const calendarOptions = {
  eventContent: (data: any) => {
    // The content for any calendar event

    const { companySettings } = useCompanyStore();
    const { event } = data;

    const isRecurringAbsence =
      event.extendedProps.type === 'absence' && !!event.extendedProps.rrule;
    const isRecurringCopy =
      event.extendedProps.rrule && !isRecurringAbsence
        ? !dayjs(event.start).isSame(event.extendedProps.originalStartAt, 'day')
        : false;

    const slotInterval = companySettings.agenda?.slotInterval;
    const isMonthView = data.view.type === 'dayGridMonth';

    const slotMinTime = dayjs
      .max(
        dayjs('2020-01-01T00:00'),
        dayjs(
          `2020-01-01T${companySettings.agenda?.startTime || '00:00'}`,
        ).subtract(2, 'hour'),
      )
      .format('HH:mm');
    const minEndTime = dayjs('2020-01-01T21:00');
    const maxEndTime = dayjs('2020-01-01T23:59');
    const minDate = dayjs.min(
      maxEndTime,
      dayjs(`2020-01-01T${companySettings.agenda?.endTime || '00:00'}`).add(
        3,
        'hour',
      ),
    );
    const slotMaxTime = dayjs.max(minEndTime, minDate).format('HH:mm');

    // Needed for multi day absences, which need to get the start or end time of the calendar depending on if the start or end date of the event is different from the absence
    const startStr = data.isStart
      ? event.startStr
      : combineDateTime(dayjs().tz().format(), slotMinTime);
    const endStr = data.isEnd
      ? event.endStr
      : combineDateTime(dayjs().tz().format(), slotMaxTime);

    const { darkerColors, theme } = useThemeStore();

    const eventContent = {
      id: event.id,
      allDay: event.allDay,
      title: event.title,
      extendedProps: event.extendedProps,
      startStr,
      endStr,
      duration: dayjs(endStr).diff(dayjs(startStr), 'minutes'),
      darkerColors,
      isDarkMode: theme === 'DARK',
      // A month day lists its events one line each in a lighter style without
      // their status icons, whatever their duration
      isMonthView,
      isSmall:
        isMonthView || event.extendedProps.eventDuration <= slotInterval * 1.35,
      isMedium:
        !isMonthView && event.extendedProps.eventDuration <= slotInterval * 2,
      features:
        isRecurringCopy || isRecurringAbsence
          ? {
              recurs: true,
              service_package: event.extendedProps.features?.service_package,
            }
          : event.extendedProps.features,
    };

    const companyBufferTime =
      (companySettings.agenda?.appointmentEndBuffer &&
        companySettings.agenda?.appointmentEndBufferTime) ||
      0;
    let eventBufferTime = 0;

    // Month view doesn't draw buffer time, so it skips this lookup, which goes
    // through every event in the calendar for each event it renders
    if (companyBufferTime > 0 && !isMonthView) {
      // When adding buffer time to the event, we need to check if the event is the last event of the appointment
      // Because only the last event of the appointment should have the buffer time
      // In order to determine if the event is the last event of the appointment, we need to get all the events for the appointment
      // And then check if the event is the last event of the appointment

      const allEvents = calendarInterface.api?.getEvents() || [];

      if (allEvents.length) {
        const allAppointmentEvents = allEvents.filter(
          (appointmentEvent: any) =>
            appointmentEvent.extendedProps.type === 'appointment' &&
            appointmentEvent.extendedProps.entryId ===
              event.extendedProps.entryId,
        );

        if (allAppointmentEvents.length === 1) {
          eventBufferTime = companyBufferTime;
        } else if (allAppointmentEvents.length > 1) {
          // getEvents() order is not guaranteed (FullCalendar); pick last by time
          const sortedAppointmentEvents = [...allAppointmentEvents].sort(
            (a: any, b: any) => {
              const byStart =
                dayjs(a.start).valueOf() - dayjs(b.start).valueOf();
              if (byStart !== 0) return byStart;
              return dayjs(a.end).valueOf() - dayjs(b.end).valueOf();
            },
          );
          const isLastEventOfAppointment =
            sortedAppointmentEvents[sortedAppointmentEvents.length - 1].id ===
            event.id;

          if (isLastEventOfAppointment) {
            eventBufferTime = companyBufferTime;
          } else {
            eventBufferTime = 0;
          }
        }
      }
    }

    return {
      html: createItem({
        event: eventContent,
        bufferTime: eventBufferTime,
      }),
    };
  },
  moreLinkContent({ num }: { num: string }) {
    return { html: `+${num} more` };
  },
  nowIndicatorContent: () => ({
    html: `<div class="now-indicator-label">${formatDate(dayjs(), { format: 'time' })}</div>`,
  }),
  dayHeaderContent: (data: any) => {
    // The content for a column header in week view, showing the day of the week
    const { viewType, resourceId } = useCalendarFiltersStore();
    const { resourceById } = useResourcesStore();
    const resource = resourceId ? resourceById(resourceId) : null;

    return {
      html: createDayHeaderContent({
        data,
        view: viewType,
        resource,
      }),
    };
  },
  dayHeaderDidMount: (info: {
    el: HTMLElement;
    date: string;
    view: { type: string };
  }) => {
    const link = info.el.querySelector('a.fc-col-header-cell-cushion');
    if (link) {
      link.removeAttribute('title');
    }

    // A month view header is a weekday, not a date to go to
    if (info.view.type === 'dayGridMonth') {
      return;
    }

    const date = filters.date(info.date, { format: 'long' });
    info.el.addEventListener('mouseenter', () => {
      showTooltip(i18n.t('calendar.go_to_date', { date }), link as HTMLElement);
    });
    info.el.addEventListener('mouseleave', () => {
      hideTooltip();
    });
  },
  resourceLabelContent: (data: any) => {
    // The content for a column header in day view, showing the resource

    const { resourceById } = useResourcesStore();
    const resource = resourceById(Number.parseInt(data.resource.id));
    if (resource) {
      return {
        html: createResourceLabel(resource),
      };
    }
  },
  slotLabelContent: ({ date }: { date: string }) =>
    formatDate(date, { format: 'time' }),
};
